import { motorNaoTratado, type Motor } from '@remoto/protocolo';

import type { TipoPedido } from './index';

/**
 * Quanto barulho cada ferramenta faz no painel.
 *
 * `automatico` aprova DENTRO do `canUseTool`, depois do guard-rail — nunca via
 * `allowedTools`, que aprovaria antes de qualquer conferência. São as
 * ferramentas de leitura e de anotação: encher a conversa de cartão de
 * permissão para `TodoWrite` e `Glob` era metade do "fica incompleto".
 *
 * `interativo` é inegociável: são as ferramentas cuja razão de existir é uma
 * decisão humana. Elas nunca entram em lista de aprovação automática, nem por
 * configuração futura — o teste desta tabela prende isso.
 *
 * O resto é `perguntar`, que é o comportamento de sempre.
 */
export type NivelDePergunta = 'automatico' | 'perguntar' | 'interativo';

/**
 * As consultas da sessão mestre (F13) que rodam sem cartão: leitura pura,
 * escopada pela API. Nomes SEM o prefixo `mcp__remoto__`, porque o Codex os
 * consome assim (a config dele é por servidor) — ADR-0021.
 *
 * **É a fonte única.** `AUTOMATICAS` é montada a partir daqui, e o Codex
 * importa esta lista, nunca uma cópia: duas listas de "o que é leitura"
 * divergiriam calada, e a divergência seria uma escrita aprovada sem ninguém.
 *
 * ATENÇÃO dupla: (1) nenhuma escrita entra aqui (`enviar_mensagem`,
 * `criar_sessao` e as outras) — despacho segue o modo da sessão; o teste
 * desta lista prende isso, com violação plantada. (2) `listar_mestres` fica
 * FORA: ela revela o par, e isso não é consulta de rotina. (3) o host só honra
 * o `automatico` destas quando a sessão É o mestre — senão um `.mcp.json` de
 * projeto forjaria um servidor "remoto" com leitura auto-aprovada.
 */
export const LEITURAS_DO_MESTRE = Object.freeze([
  'listar_maquinas',
  'listar_sessoes',
  'ler_conversa',
  'buscar',
  'listar_trabalhos',
  'ler_config_claude',
  'listar_pendencias',
] as const);
export type LeituraDoMestre = (typeof LEITURAS_DO_MESTRE)[number];

/** O prefixo que o Claude Code põe nas ferramentas do servidor MCP `remoto`. */
export const PREFIXO_MCP_REMOTO = 'mcp__remoto__';

/**
 * As ferramentas do servidor MCP `toolkit` (F3 da revisão de out/2026), SEM o
 * prefixo — a fonte única: o daemon as serve com estes nomes, e um spec do
 * daemon confere os dois lados.
 */
export const FERRAMENTAS_DO_TOOLKIT = Object.freeze([
  'toolkit_buscar',
  'toolkit_ler',
  'toolkit_executar',
  'toolkit_propor',
] as const);

/**
 * As do toolkit que só LEEM o catálogo. No Codex elas são as únicas com
 * `approval_mode = "approve"` e `readOnlyHint` (F3.3); `toolkit_executar` e
 * `toolkit_propor` vão `prompt`. Importada pelo daemon, nunca copiada — a
 * regra da `LEITURAS_DO_MESTRE`.
 */
export const LEITURAS_DO_TOOLKIT = Object.freeze(['toolkit_buscar', 'toolkit_ler'] as const);

/** O prefixo das ferramentas do `toolkit` no Claude Code. */
export const PREFIXO_MCP_TOOLKIT = 'mcp__toolkit__';

const AUTOMATICAS = new Set([
  'TodoWrite',
  'Read',
  'Glob',
  'Grep',
  'TaskCreate',
  'TaskGet',
  'TaskUpdate',
  'TaskList',
  'TaskOutput',
  'ListMcpResources',
  'ReadMcpResource',
  'ReadMcpResourceDir',
  'ReadNotifications',
  'ScheduleWakeup',
  // As consultas da sessão mestre (F13) vêm de `LEITURAS_DO_MESTRE`, acima —
  // a regra e os avisos moram lá.
  ...LEITURAS_DO_MESTRE.map((nome) => `${PREFIXO_MCP_REMOTO}${nome}`),
  // As consultas do cérebro (F14). Mesma regra em dobro, e pela mesma razão:
  // o host só honra o `automatico` de `mcp__cerebro__*` quando foi ELE quem
  // injetou o servidor naquela sessão — senão um `.mcp.json` de projeto
  // forjaria um "cerebro" homônimo e herdaria a aprovação.
  //
  // **`mcp__cerebro__hermes_run` e `mcp__cerebro__memory_anotar` JAMAIS entram
  // aqui**, e o teste desta tabela prende os dois: o primeiro gasta crédito e
  // manda trechos de conversa para fora de casa; o segundo escreve. Leitura
  // pode ser automática; efeito, não.
  //
  // A lista é o que o bridge SERVE, conferido contra
  // `apps/cerebro/contrato/ferramentas.json` por um spec de cada lado.
  // `buscar_docs` e `memory_buscar` estiveram aqui por meses **sem existir lá**:
  // aprovar um nome que ninguém serve não faz mal sozinho — a chamada falha —,
  // mas faz a lista deixar de significar "conferi que é leitura pura" e passar
  // a significar palpite. E é o palpite que um `.mcp.json` forjando um
  // "cerebro" homônimo procura. Elas voltam no dia em que a ferramenta existir.
  'mcp__cerebro__buscar_dialogos',
  'mcp__cerebro__memory_ler',
  'mcp__cerebro__cliente_contexto',
  'mcp__cerebro__saude',
  // O toolkit (F3 da revisão de out/2026, ADR-0026): as quatro, e o host só
  // honra o `automatico` quando foi ELE quem injetou o servidor (medido: com o
  // injetado presente, o homônimo de `.mcp.json` não responde —
  // `F3-homonimo.md`; sem ele, o forjado sobe sozinho).
  //
  // **`toolkit_executar` entra, e é a ÚNICA ferramenta de efeito desta lista,
  // de propósito:** o portão dela é o CARTÃO DO EXECUTOR, decidido pela classe
  // da fase dentro do handler (`apps/agent/src/toolkit/executor.ts`, D21 +
  // D30) — e lá ele vale igual em todo modo, inclusive `auto` e `bypass`, em
  // que o `canUseTool` nem roda. Um cartão aqui também faria a LEITURA pedir
  // cartão em `default` (contra a D30) e a escrita pedir dois.
  // `toolkit_propor` só cria a proposta; quem decide é a tela (D19).
  ...FERRAMENTAS_DO_TOOLKIT.map((nome) => `${PREFIXO_MCP_TOOLKIT}${nome}`),
]);

/**
 * A allowlist, só para LER — uma cópia, que ninguém altera por aqui.
 *
 * Existe para os contratos varrerem a lista INTEIRA. Perguntando o nível nome a
 * nome, o teste só pegava os nomes que alguém lembrou de chutar: um
 * `mcp__cerebro__listar_notas` que o bridge não serve entrava aprovado e o
 * contrato seguia verde (revisão do PR 58, T6).
 */
export function ferramentasAutomaticas(): readonly string[] {
  return [...AUTOMATICAS];
}

const INTERATIVAS = new Set([
  'AskUserQuestion',
  'ExitPlanMode',
  'EnterPlanMode',
  'ProposeGoal',
  'ProposeSkills',
  'ExitWorktree',
  'SendFeedback',
  'Artifact',
  'CronCreate',
  'RemoteTrigger',
]);

/**
 * **As duas tabelas acima são nomes de ferramenta do CLAUDE, e só.**
 *
 * `AUTOMATICAS` e `INTERATIVAS` casam por NOME. Um servidor MCP qualquer, ou o
 * outro CLI, pode expor uma ferramenta chamada `Read` que não é a nossa — e
 * casar por nome a aprovaria sozinha, sem cartão, sem log e sem ninguém no
 * meio. É a mesma armadilha que já obrigou o host a exigir `ehMestre` antes de
 * honrar o `automatico` de `mcp__remoto__*`.
 *
 * Por isso o motor é parâmetro OBRIGATÓRIO e não tem `.default('CLAUDE')`: com
 * default, um chamador novo que esquecesse de passar herdaria a allowlist do
 * Claude para o outro motor, e o compilador ficaria calado. Obrigatório, a
 * violação não compila — que é a única forma de guarda que ninguém esquece.
 */
export function nivelDePergunta(ferramenta: string, motor: Motor): NivelDePergunta {
  /*
    `switch` exaustivo, e o terceiro motor é a razão: o `if (motor === 'CODEX')`
    que existia aqui mandava o OPENCODE para as tabelas do Claude, e um `Read`
    do OpenCode sairia AUTO-APROVADO, sem cartão e sem ninguém (levantamento de
    2026-09-23). Com o `switch`, um quarto motor não compila até alguém decidir
    nesta função o que ele herda — e a resposta certa é quase sempre "nada".
  */
  switch (motor) {
    case 'CLAUDE':
      if (INTERATIVAS.has(ferramenta)) return 'interativo';
      if (AUTOMATICAS.has(ferramenta)) return 'automatico';
      return 'perguntar';
    case 'CODEX':
      return 'perguntar';
    case 'OPENCODE':
      /*
        O OpenCode só chega aqui com o que a camada de permissão DELE mandou
        perguntar (`permission.asked`): o que é leitura já foi liberado pelas
        regras da SESSÃO que o daemon escreve (R1: o modo é o conjunto de
        regras de `POST`/`PATCH /session`, e a última que casa vence), e o que
        chegou é, por definição, o que alguém decidiu que precisa de gente.
        Aprovar por nome aqui desfaria essa decisão com uma tabela escrita para
        outro CLI.
      */
      return 'perguntar';
    default:
      return motorNaoTratado(motor);
  }
}

/**
 * O tipo de cartão que o pedido vira no painel.
 *
 * Pela MESMA razão do `nivelDePergunta`, no Codex nada é decidido por nome:
 * `AskUserQuestion` não existe lá (o equivalente é a requisição
 * `item/tool/requestUserInput`), então um nome desses numa sessão Codex é
 * ferramenta de terceiro homônima — desenhar um cartão de PERGUNTA para ela
 * ofereceria campos de resposta que o outro lado não sabe ler.
 *
 * Quem decide o tipo de um pedido do Codex é o MÉTODO do `ServerRequest`, lido
 * pelo tradutor do daemon, não esta função.
 */
export function tipoDoPedido(ferramenta: string, motor: Motor): TipoPedido {
  switch (motor) {
    case 'CLAUDE':
      if (ferramenta === 'AskUserQuestion') return 'PERGUNTA';
      if (ferramenta === 'ExitPlanMode') return 'PLANO';
      if (ferramenta === 'ProposeGoal' || ferramenta === 'ProposeSkills') return 'PROPOSTA';
      return 'FERRAMENTA';
    case 'CODEX':
      return 'FERRAMENTA';
    case 'OPENCODE':
      /*
        Mesma razão do Codex: no OpenCode a pergunta chega por EVENTO
        (`question.asked`), não por nome de ferramenta — quem marca PERGUNTA é
        o tradutor do daemon, lendo o evento. Uma ferramenta de terceiro
        chamada `AskUserQuestion` numa sessão OpenCode é só isso: uma
        ferramenta, e o cartão é de permissão.
      */
      return 'FERRAMENTA';
    default:
      return motorNaoTratado(motor);
  }
}
